/**
 * PandaDoc Monitor Hook
 *
 * Detects when BAA signing is complete via Chrome extension.
 * Similar to useStripeCheckoutMonitor but for PandaDoc BAA signing.
 *
 * This hook ONLY detects success - opening PandaDoc is handled by useBAASigning.
 */

import { useEffect, useRef, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-pandadoc-monitor');

/**
 * Detect if Chrome extension is available
 */
function detectExtension(): boolean {
  // Check if chrome.runtime is available
  if (typeof chrome !== 'undefined' && chrome.runtime?.id) {
    return true;
  }
  return false;
}

export type PandaDocMonitorConfig = {
  /**
   * Callback when BAA signing is detected as complete
   */
  onSuccess?: (sessionId: string | null) => void;

  /**
   * Enable debug logging
   */
  debug?: boolean;
};

export type PandaDocMonitor = {
  /**
   * Check if Chrome extension is available
   */
  hasExtension: boolean;
};

/**
 * Hook to monitor PandaDoc BAA signing with extension
 */
export const usePandaDocMonitor = (config: PandaDocMonitorConfig = {}): PandaDocMonitor => {
  const { onSuccess, debug = false } = config;

  const [hasExtension] = useState(detectExtension());
  const successCallbackRef = useRef(onSuccess);

  // Update refs when callbacks change
  useEffect(() => {
    successCallbackRef.current = onSuccess;
  }, [onSuccess]);

  // Log extension availability on mount
  useEffect(() => {
    if (debug) {
      if (hasExtension) {
        logger.info(
          '🎯 [PANDADOC-MONITOR] Extension detected - BAA signing will be monitored automatically'
        );
      } else {
        logger.warn(
          '⚠️ [PANDADOC-MONITOR] No extension detected - BAA completion must be detected via polling'
        );
      }
    }
  }, [hasExtension, debug]);

  // Listen for success messages from extension
  useEffect(() => {
    if (!hasExtension) return undefined;

    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our extension
      if (event.source !== window) return;

      const message = event.data;
      if (message.type === 'PANDADOC_SUCCESS') {
        if (debug) {
          logger.info('✅ [PANDADOC-MONITOR] BAA signing success notification from extension:', {
            sessionId: message.sessionId,
          });
        }

        successCallbackRef.current?.(message.sessionId);
      }
    };

    window.addEventListener('message', handleMessage);

    // Also listen for chrome.runtime messages (if in extension context)
    let cleanupRuntimeListener: (() => void) | undefined;

    if (chrome.runtime?.onMessage) {
      const runtimeListener = (
        message: any,
        _sender: chrome.runtime.MessageSender,
        _sendResponse: (response?: any) => void
      ) => {
        if (message.type === 'PANDADOC_SUCCESS') {
          if (debug) {
            logger.info('✅ [PANDADOC-MONITOR] BAA signing success from extension runtime:', {
              sessionId: message.sessionId,
            });
          }

          successCallbackRef.current?.(message.sessionId);
        }
      };

      chrome.runtime.onMessage.addListener(runtimeListener);
      cleanupRuntimeListener = () => {
        chrome.runtime.onMessage.removeListener(runtimeListener);
      };
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      cleanupRuntimeListener?.();
    };
  }, [hasExtension, debug]);

  return {
    hasExtension,
  };
};
